import { getDb } from "../db/database";
import type { AnalyticsQueueRecord } from "../db/schema";
import {
  ANALYTICS_MAX_BATCH_BYTES,
  ANALYTICS_MAX_BATCH_EVENTS,
  ANALYTICS_MAX_EVENTS,
  ANALYTICS_MAX_QUEUE_BYTES
} from "./analyticsConfig";
import type { AnalyticsEvent, AnalyticsPriority } from "./analyticsTypes";

const memoryQueue: AnalyticsQueueRecord[] = [];

export async function enqueueAnalyticsEvent(event: AnalyticsEvent, priority: AnalyticsPriority): Promise<void> {
  const record: AnalyticsQueueRecord = {
    eventId: event.eventId,
    event,
    queuedAt: new Date().toISOString(),
    priority,
    approximateBytes: approximateBytes(event)
  };

  try {
    const db = await getDb();
    await db.put("analyticsQueue", record);
    await enforceQueueCaps();
  } catch {
    enqueueMemory(record);
  }
}

export async function peekAnalyticsBatch(): Promise<AnalyticsQueueRecord[]> {
  const memoryBatch = takeMemoryBatch(false);
  if (memoryBatch.length) {
    return memoryBatch;
  }

  try {
    const db = await getDb();
    const records = await db.getAllFromIndex("analyticsQueue", "byQueuedAt");
    const batch: AnalyticsQueueRecord[] = [];
    let bytes = 0;
    for (const record of records) {
      const nextBytes = bytes + record.approximateBytes;
      if (batch.length >= ANALYTICS_MAX_BATCH_EVENTS || nextBytes > ANALYTICS_MAX_BATCH_BYTES) {
        break;
      }
      batch.push(record);
      bytes = nextBytes;
    }
    return batch;
  } catch {
    return takeMemoryBatch(false);
  }
}

export async function deleteAnalyticsEvents(eventIds: readonly string[]): Promise<void> {
  if (!eventIds.length) {
    return;
  }
  removeMemoryEvents(eventIds);
  try {
    const db = await getDb();
    const tx = db.transaction("analyticsQueue", "readwrite");
    await Promise.all(eventIds.map((eventId) => tx.objectStore("analyticsQueue").delete(eventId)));
    await tx.done;
  } catch {
    removeMemoryEvents(eventIds);
  }
}

export async function clearAnalyticsQueue(): Promise<void> {
  memoryQueue.splice(0, memoryQueue.length);
  try {
    const db = await getDb();
    await db.clear("analyticsQueue");
  } catch {
    memoryQueue.splice(0, memoryQueue.length);
  }
}

async function enforceQueueCaps(): Promise<void> {
  const db = await getDb();
  const records = await db.getAll("analyticsQueue");
  let totalBytes = records.reduce((sum, record) => sum + record.approximateBytes, 0);
  let totalEvents = records.length;
  if (totalEvents <= ANALYTICS_MAX_EVENTS && totalBytes <= ANALYTICS_MAX_QUEUE_BYTES) {
    return;
  }

  const removable = [...records].sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority === "normal" ? -1 : 1;
    }
    return a.queuedAt.localeCompare(b.queuedAt);
  });
  const tx = db.transaction("analyticsQueue", "readwrite");
  for (const record of removable) {
    if (totalEvents <= ANALYTICS_MAX_EVENTS && totalBytes <= ANALYTICS_MAX_QUEUE_BYTES) {
      break;
    }
    await tx.objectStore("analyticsQueue").delete(record.eventId);
    totalEvents -= 1;
    totalBytes -= record.approximateBytes;
  }
  await tx.done;
}

function enqueueMemory(record: AnalyticsQueueRecord): void {
  memoryQueue.push(record);
  while (memoryQueue.length > ANALYTICS_MAX_EVENTS) {
    const removableIndex = memoryQueue.findIndex((item) => item.priority === "normal");
    memoryQueue.splice(removableIndex >= 0 ? removableIndex : 0, 1);
  }
}

function takeMemoryBatch(remove: boolean): AnalyticsQueueRecord[] {
  const batch: AnalyticsQueueRecord[] = [];
  let bytes = 0;
  for (const record of memoryQueue) {
    if (batch.length >= ANALYTICS_MAX_BATCH_EVENTS || bytes + record.approximateBytes > ANALYTICS_MAX_BATCH_BYTES) {
      break;
    }
    batch.push(record);
    bytes += record.approximateBytes;
  }
  if (remove) {
    removeMemoryEvents(batch.map((record) => record.eventId));
  }
  return batch;
}

function removeMemoryEvents(eventIds: readonly string[]): void {
  const ids = new Set(eventIds);
  for (let index = memoryQueue.length - 1; index >= 0; index -= 1) {
    if (ids.has(memoryQueue[index].eventId)) {
      memoryQueue.splice(index, 1);
    }
  }
}

function approximateBytes(value: unknown): number {
  return new Blob([JSON.stringify(value)]).size;
}
